import styled from "@emotion/styled";
import { Box } from "@mui/system";

// Page Layout
export const PageContainer = styled(Box)`
  width: 100%;
  min-height: 100%;
  background-color: #f8f9fa;
  padding: 16px;

  @media only screen and (min-width: 768px) {
    padding: 24px;
  }
`;

export const ContentWrapper = styled(Box)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

// Header Section
export const HeaderSection = styled(Box)`
  margin-bottom: 24px;
`;

export const TitleRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

export const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;

  @media only screen and (min-width: 768px) {
    font-size: 28px;
  }
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

// Search
export const SearchInputBox = styled(Box)`
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  height: 44px;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #4b87ed;
    box-shadow: 0 0 0 3px rgba(75, 135, 237, 0.1);
  }

  @media only screen and (min-width: 768px) {
    width: 320px;
  }
`;

export const SearchIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

// Table Components
export const TableWrapper = styled(Box)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

export const TableHeader = styled(Box)`
  display: none;
  padding: 14px 20px;
  background-color: #e7e9ed;
  border-bottom: 1px solid #e5e7eb;

  @media only screen and (min-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

export const TableHeaderCell = styled.span<{ width?: string }>`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: ${({ width }) => width || "auto"};
  flex-shrink: 0;
`;

export const TableRow = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.15s;
  gap: 8px;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media only screen and (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    padding: 14px 20px;
    gap: 0;
  }
`;

export const TableCell = styled.span<{ width?: string; mobileLabel?: string }>`
  font-size: 14px;
  color: #374151;
  width: 100%;

  &::before {
    content: "${({ mobileLabel }) => mobileLabel || ""}";
    display: ${({ mobileLabel }) => (mobileLabel ? "block" : "none")};
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  @media only screen and (min-width: 768px) {
    width: ${({ width }) => width || "auto"};
    flex-shrink: 0;

    &::before {
      display: none;
    }
  }
`;

export const PrimaryCell = styled(TableCell)`
  font-weight: 500;
  color: #1a1a2e;
`;

// Badges
export const StatusBadge = styled.span<{ variant?: "success" | "info" | "warning" | "default" }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "#dcfce7";
      case "info":
        return "#dbeafe";
      case "warning":
        return "#fef3c7";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "#166534";
      case "info":
        return "#1e40af";
      case "warning":
        return "#92400e";
      default:
        return "#374151";
    }
  }};
`;

// Text Variants
export const SecondaryText = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

// Pagination
export const PaginationWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  gap: 8px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;

  @media only screen and (min-width: 768px) {
    justify-content: flex-end;
    padding: 14px 20px;
  }
`;

export const PaginationText = styled.span`
  font-size: 14px;
  color: #6b7280;
  margin: 0 8px;
`;

// Empty State
export const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

export const EmptyStateText = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0;
`;

// View Link
export const ViewLink = styled.span`
  color: #4b87ed;
  font-size: 13px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;
